/**
 * VarslingSystem - Browser notifications for kritiske oppgaver og frister
 *
 * Viser pop-up hver time med:
 * - Forfalte oppgaver (viktig/absolutt)
 * - Forfalte frister (viktig/absolutt)
 * - Dagens frister (viktig/absolutt)
 */

import React, { useState, useEffect, useRef } from 'react';
import crmApi from '../api/crm';

export default function VarslingSystem() {
  const [varsler, setVarsler] = useState([]);
  const [tillatelser, setTillatelser] = useState('default');
  const [visVarslingPanel, setVisVarslingPanel] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Sjekk notification-tillatelser
    if ('Notification' in window) {
      setTillatelser(Notification.permission);
    }

    // Start hourly check
    startVarslingIntervall();

    // Kjør første sjekk umiddelbart
    sjekkVarsler();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startVarslingIntervall = () => {
    // Kjør hver time
    intervalRef.current = setInterval(() => {
      sjekkVarsler();
    }, 60 * 60 * 1000); // 1 time
  };

  const sjekkVarsler = async () => {
    try {
      const response = await crmApi.request('/api/varsler/kritiske');
      const { varsler: nyeVarsler, totalt, nye } = response;

      if (nye > 0) {
        setVarsler(nyeVarsler);
        setVisVarslingPanel(true);

        // Send browser notifications
        if (tillatelser === 'granted') {
          sendBrowserNotifications(nyeVarsler);
        }
      }
    } catch (error) {
      console.error('Feil ved sjekk av varsler:', error);
    }
  };

  const sendBrowserNotifications = (varslerListe) => {
    // Grupper etter type
    const forfalteOppgaver = varslerListe.filter(v => v.varsel_type === 'oppgave_forfalt');
    const forfalteFrister = varslerListe.filter(v => v.varsel_type === 'frist_forfalt');
    const fristerIdag = varslerListe.filter(v => v.varsel_type === 'frist_idag');
    const oppgaverIdag = varslerListe.filter(v => v.varsel_type === 'oppgave_idag');

    // Send samlet varsling for hver kategori
    if (forfalteOppgaver.length > 0) {
      new Notification('🚨 Forfalte oppgaver', {
        body: `Du har ${forfalteOppgaver.length} forfalte oppgaver som trenger oppmerksomhet.`,
        icon: '/logo192.png',
        tag: 'forfalte-oppgaver',
        requireInteraction: true // Vises til bruker lukker
      });
    }

    if (forfalteFrister.length > 0) {
      new Notification('⏰ Forfalte frister', {
        body: `Du har ${forfalteFrister.length} forfalte frister!`,
        icon: '/logo192.png',
        tag: 'forfalte-frister',
        requireInteraction: true
      });
    }

    if (fristerIdag.length > 0) {
      new Notification('📅 Frister i dag', {
        body: `Du har ${fristerIdag.length} frist(er) som må håndteres i dag.`,
        icon: '/logo192.png',
        tag: 'frister-idag'
      });
    }

    if (oppgaverIdag.length > 0) {
      new Notification('✅ Oppgaver i dag', {
        body: `Du har ${oppgaverIdag.length} oppgave(r) med frist i dag.`,
        icon: '/logo192.png',
        tag: 'oppgaver-idag'
      });
    }
  };

  const foresporrTillatelse = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setTillatelser(permission);

      if (permission === 'granted') {
        new Notification('✅ Varslinger aktivert', {
          body: 'Du vil nå motta viktige varsler fra AdvokatCRM.',
          icon: '/logo192.png'
        });
      }
    }
  };

  const lukkVarslingPanel = () => {
    setVisVarslingPanel(false);
  };

  const markerSomLest = async (varslingId) => {
    try {
      await crmApi.request(`/api/varsler/${varslingId}/lest`, {
        method: 'POST'
      });

      // Fjern fra liste
      setVarsler(varsler.filter(v => v.id !== varslingId));
    } catch (error) {
      console.error('Feil ved markering som lest:', error);
    }
  };

  const getKritiskhetBadge = (kritiskhet) => {
    const styles = {
      absolutt: { bg: '#fee2e2', color: '#991b1b', label: '🚨 ABSOLUTT' },
      viktig: { bg: '#fef3c7', color: '#92400e', label: '⚠️ VIKTIG' },
      normal: { bg: '#e5e7eb', color: '#1f2937', label: 'Normal' }
    };

    const style = styles[kritiskhet] || styles.normal;

    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 'bold'
      }}>
        {style.label}
      </span>
    );
  };

  const getVarselTypeLabel = (type) => {
    const labels = {
      oppgave_forfalt: '📋 Forfalt oppgave',
      frist_forfalt: '⏰ Forfalt frist',
      frist_idag: '📅 Frist i dag',
      oppgave_idag: '✅ Oppgave i dag'
    };
    return labels[type] || type;
  };

  return (
    <div className="varsling-system">
      {/* Varsling-indikator i header */}
      {varsler.length > 0 && (
        <button
          onClick={() => setVisVarslingPanel(!visVarslingPanel)}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            fontSize: '1.5rem',
            cursor: 'pointer',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 9999,
            animation: 'pulse 2s infinite'
          }}
          title={`${varsler.length} kritisk(e) varsler`}
        >
          🔔
          <span style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            backgroundColor: 'white',
            color: '#ef4444',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold'
          }}>
            {varsler.length}
          </span>
        </button>
      )}

      {/* Varsling-panel */}
      {visVarslingPanel && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          width: '400px',
          maxHeight: '600px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          zIndex: 9998,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fef2f2'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#991b1b' }}>
              🚨 Kritiske varsler ({varsler.length})
            </h3>
            <button
              onClick={lukkVarslingPanel}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6b7280'
              }}
            >
              ×
            </button>
          </div>

          {/* Notification permission */}
          {tillatelser !== 'granted' && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderBottom: '1px solid #fde68a',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '0.9rem', flex: 1 }}>
                Aktiver varslinger for å motta pop-ups hver time
              </span>
              <button
                onClick={foresporrTillatelse}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Aktiver
              </button>
            </div>
          )}

          {/* Varsler-liste */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px'
          }}>
            {varsler.map((varsel, idx) => (
              <div
                key={idx}
                style={{
                  padding: '12px',
                  backgroundColor: varsel.kritiskhet === 'absolutt' ? '#fef2f2' : '#fffbeb',
                  border: `1px solid ${varsel.kritiskhet === 'absolutt' ? '#fecaca' : '#fde68a'}`,
                  borderRadius: '6px',
                  marginBottom: '8px'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>
                      {getVarselTypeLabel(varsel.varsel_type)}
                    </div>
                    <strong style={{ fontSize: '0.95rem' }}>{varsel.tittel}</strong>
                  </div>
                  {getKritiskhetBadge(varsel.kritiskhet)}
                </div>

                {varsel.sak_saksnummer && (
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                    Sak: {varsel.sak_saksnummer}
                  </div>
                )}

                <button
                  onClick={() => markerSomLest(varsel.id)}
                  style={{
                    marginTop: '8px',
                    padding: '4px 8px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  ✓ Marker som lest
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}
